import {
  AfterViewInit,
  Component,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  viewChild,
  viewChildren,
} from '@angular/core';
import { EMPTY, of } from 'rxjs';
import { catchError, first, switchMap, tap } from 'rxjs/operators';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { UserService } from '../../../../../../core/services/user/user.service';
import { LotteryService } from '../../../../../../core/services/lottery.service';
import { UserBonusesService } from '../../../../../../core/services/user/user-bonuses.service';
import { ToastMessageService } from '../../../../../../core/modules/toast-message/toast-message.service';
import { FormsErrorHandlerService } from '../../../../../../core/services/forms-error-handler.service';
import { LocalstorageService } from '../../../../../../core/services/localstorage.service';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { SliderComponent } from '../../../../../../core/modules/slider/slider.component';
import { TranslatePipe } from '../../../../../../core/shared/translation/translate.pipe';
import { SafePipe } from 'ngx-unificator/pipes';
import { PreloaderComponent } from '../../../../../../core/shared/components/preloader/preloader.component';
import {
  FormCheckboxComponent,
} from '../../../../../../core/modules/form-controls/form-checkbox/form-checkbox.component';
import { FormInputComponent } from '../../../../../../core/modules/form-controls/form-input/form-input.component';
import { CookieService } from 'ngx-unificator/services';
import { AB_TEST_LIST } from '../../../../../../core/ab-test/ab-test.data';
import { DepositReloadBonusesComponent } from '../deposit-reload-bonuses/deposit-reload-bonuses.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ReloadBonusesService } from '../../../../../../core/services/reload-bonuses.service';
import { CLEAR_BONUS_CODE } from '../../../../../../core/services/user/data/user-group.data';
import { ScrollService } from '../../../../../../core/services/scroll.service';
import { untilDestroyed } from 'ngx-unificator/rxjs';
import { UntilDestroy } from 'ngx-unificator/decorator';
import { GlobalEventsService } from '../../../../../../core/services/global-events.service';
import { AccordionDirective } from 'ngx-unificator/directives';

const STORAGE_BONUSES_NAME = '--deposit-bonuses';

@UntilDestroy()
@Component({
    selector: 'app-deposit-bonus',
    templateUrl: './deposit-bonus.component.html',
    styleUrls: ['./deposit-bonus.component.scss'],
    imports: [
        AsyncPipe,
        SliderComponent,
        NgClass,
        TranslatePipe,
        SafePipe,
        NgTemplateOutlet,
        PreloaderComponent,
        FormCheckboxComponent,
        ReactiveFormsModule,
        AccordionDirective,
        FormInputComponent,
        DepositReloadBonusesComponent,
    ]
})
export class DepositBonusComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  user = inject(UserService);
  lottery = inject(LotteryService);
  private _toastMessage = inject(ToastMessageService);
  private _userBonus = inject(UserBonusesService);
  private _fb = inject(UntypedFormBuilder);
  private _formErrors = inject(FormsErrorHandlerService);
  private _storage = inject(LocalstorageService);
  private _cookie = inject(CookieService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _reloadBonuses = inject(ReloadBonusesService);
  private _scroll = inject(ScrollService);
  private _events = inject(GlobalEventsService);

  readonly bonusCode = input<string>(undefined);

  readonly _accordion = viewChildren(AccordionDirective);

  readonly bonusAccordion = viewChild('bonusAccordion', { read: AccordionDirective });
  public bonuscodeForm = signal<any>(null);

  public showSlider = signal(false);

  public depositBonuses$ = this._getActiveBonuses();

  sliderConfig = {
    loop: true,
    breakpoints: {
      '(max-width: 720px)': {
        slides: {
          perView: 1,
          spacing: 12,
        },
      },
      '(min-width: 721px)': {
        slides: {
          spacing: 10,
        },
      },
    }
  };

  ngOnInit() {
    this._initBonuscodeForm(this._cookie.get('bonuscode')?.trim());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.bonusCode && changes.bonusCode.firstChange && changes.bonusCode.currentValue) {
      const bonusCode = changes.bonusCode.currentValue.trim();
      this._initBonuscodeForm(bonusCode);
    }
  }

  ngAfterViewInit() {
    this._listenNavEnd();
  }

  ngOnDestroy() {
    this._storage.clearItem(STORAGE_BONUSES_NAME);
  }

  /**
   * Change issue bonus
   */
  onChangeIssueBonus(event) {
    this._userBonus
      .changeIssues(event.target.checked)
      .pipe(
        switchMap(() => this.user.getUserInfo()),
        switchMap(() => this._userBonus.updateDepositBonuses()),
      )
      .subscribe();
  }

  /**
   * Send deposit bonus code
   */
   onBonuscodeFormSubmit() {
    if (!this.bonuscodeForm().get('code')?.value) {
      this._updateBonuscodeForm();
      return;
    }
    this._userBonus.activateDepositBonusCode(this.bonuscodeForm().get('code')?.value).pipe(
      catchError(error => {
        this._toastMessage.error(this._formErrors.ssBackendErrorsToArray(error.error)[0] || 't.went-wrong');
        return of(error);
      }),
      switchMap(() => this.user.getUserInfo()),
      switchMap(() => this._getActiveBonuses(true)),
      tap((bonus) => {
        this.bonuscodeForm().get('code').setValue('');
        this._updateBonuscodeForm();
        this._toastMessage.success('t.bonus-code-added');
      }),
    ).subscribe();
  }

  /**
   * Delete deposit bonus code
   */
  async deleteDepositeBonus(ignoreScroll?: boolean) {
    await this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { bonusCode: null },
      replaceUrl: true,
    });
    this._reloadBonuses.onClearSelectedBonuses();
    this._userBonus.deleteDepositBonusCode().pipe(
      switchMap(() => this.user.getUserInfo()),
      tap(() => {
        if (!ignoreScroll) {
          this._scroll.scrollToElement(this._scroll.documentElement, 600, true, 400);
        }
      }),
      switchMap(() => this._getActiveBonuses(true)),
      tap((bonus) => {
        this._updateBonuscodeForm();
      })
    ).subscribe();
  }

  private _initBonuscodeForm(bonusCode: string = '') {
    if (!this.bonuscodeForm()) {
      this.bonuscodeForm.set(
        this._fb.group({
          code: [bonusCode],
        })
      );
    }
  }

  /**
   * Update bonus code value from user info
   */
  private _updateBonuscodeForm() {
    const input = this.bonuscodeForm().get('code');
    if (this.user.info.deposit_bonus_code) {
      input.setValue(this.user.info.deposit_bonus_code);
      input.disable();
    } else {
      input.setValue('');
      input.markAsUntouched();
      input.enable();
    }
  }

  private _getActiveBonuses(isUpdateDepositList = false) {
    this.showSlider.set(false);
    this.depositBonuses$ = EMPTY;
    return (isUpdateDepositList ? this._userBonus.updateDepositBonuses() : this._userBonus.depositBonusList()).pipe(
      tap((bonuses) => {
        this.depositBonuses$ = of(bonuses);
        this.showSlider.set(false);
      }),
      tap(() => {
        setTimeout(() => {
          this.showSlider.set(true);
        }, 600);
      })
    );
  }

  private _listenNavEnd() {
    this._events.routerNavigationEnd$.pipe(
      untilDestroyed(this),
      first(),
      tap(() => this._resolveBonusCode()),
    ).subscribe();
  }

  private async _resolveBonusCode() {
    const bonusCode = this._route.snapshot.queryParamMap.get('bonusCode');
    if (bonusCode === CLEAR_BONUS_CODE) {
      return await this.deleteDepositeBonus(true);
    }
    await this._reloadBonuses.onChooseBonus(bonusCode);
    this.bonuscodeForm().get('code')?.setValue(bonusCode);
    this.onBonuscodeFormSubmit();

    bonusCode ? this.bonusAccordion().open() : this.bonusAccordion().close();
  }

  protected readonly AB_TEST_LIST = AB_TEST_LIST;
}
